//#region Types
export type SingleEventEmitterT<T = undefined> = {
  emit: CallbackHackWrapperT<CallbackT<T>>;
  on: (listener: CallbackHackWrapperT<CallbackT<T>>) => () => void;
  once: (listener: CallbackHackWrapperT<CallbackT<T>>) => () => void;
  /**
   * Turn off event or all events when receive no values
   */
  off: (listener?: CallbackHackWrapperT<CallbackT<T>>) => void;
  listenerCount: () => number;

  /**
   * Defer calling listerns until release
   */
  transaction<T extends void | Promise<void>>(cb: () => T): T;
};

type CallbackT<T> = (_: T) => void;

/**
 * Was `type CallbackT<T> = T extends undefined ? () => void : (_: T) => void;`
 * It seems with `T` is `A | B` it gets converted to `A & B`
 * @see https://mariusschulz.com/blog/conditional-types-in-typescript
 *
 * So we use this ugly hack to infer from inference
 */
type CallbackHackWrapperT<T extends (_: any) => void> =
  T extends (a: infer A) => void ?
    A extends undefined ?
      () => void
    : T
  : never;
//#endregion

/**
 * We create as object so functions want need bind.
 * So we can doing this thing:
 *
 * @example
 * class MyClass {
 *   private _blahEmitter = createSingleEventEmitter();
 *   onBlah = this._blahEmitter.on;
 * }
 */
export function createSingleEventEmitter<T = undefined>(): SingleEventEmitterT<T> {
  let transactionEvents: T[] = [];
  let transactionCounter = 0;

  const listeners = new Set<CallbackT<T>>();
  const onceListeners = new Set<CallbackT<T>>();

  function removeListener(listener: CallbackT<T>) {
    listeners.delete(listener);
    onceListeners.delete(listener);
  }

  function emitEvent(event: T) {
    listeners.forEach((listener) => {
      listener(event);
    });
  }

  function emit(event: T) {
    // Durring transaction logic
    if (transactionCounter > 0) {
      transactionEvents.push(event);
    }

    // Normal logic
    else {
      emitEvent(event);

      // Removing once listeners
      onceListeners.forEach((listener) => {
        listeners.delete(listener);
      });
      onceListeners.clear();
    }
  }

  function release(isError: boolean) {
    transactionCounter -= 1;

    if (isError) {
      transactionEvents = [];
    }

    // Making sure we don't go below `0` if it `release()` called more than `transaction()`
    if (transactionCounter < 0) transactionCounter = 0;

    // emit events in queue
    if (transactionCounter === 0) {
      const events = transactionEvents;
      transactionEvents = [];

      for (let event of events) {
        emit(event);
      }
    }
  }

  function isPromise(value: void | Promise<void>): value is Promise<void> {
    return value != null;
  }

  function transaction(cb: () => void): undefined;
  function transaction(cb: () => Promise<void>): Promise<void>;
  function transaction<T extends void | Promise<void>>(cb: () => T) {
    try {
      transactionCounter += 1;
      var promise = cb();
    } catch (error) {
      release(true);
      throw error;
    }

    if (isPromise(promise)) {
      return promise.then(
        (): void => release(false),
        (error: any) => {
          release(true);
          return Promise.reject(error);
        },
      );
    } else {
      return release(false);
    }
  }

  return {
    transaction,

    emit: emit as CallbackHackWrapperT<CallbackT<T>>,

    on: (listener) => {
      listeners.add(listener);
      return () => removeListener(listener);
    },

    once: (listener) => {
      listeners.add(listener);
      onceListeners.add(listener);
      return () => removeListener(listener);
    },

    off: (listener) => {
      if (listener == null) {
        listeners.clear();
        onceListeners.clear();
      } else {
        removeListener(listener);
      }
    },

    listenerCount: () => {
      return listeners.size;
    },
  };
}
